/**
 * The settings page (decision 16: editor themes, fonts, focus mode, keybindings; decision 66: the auto-conversion switch;
 * decision 77 and part 11e: a page of its own; part 05 notes: book titles normalised; part 11 log, tables G and K; part
 * 11e log, tables SP and SL). A section list beside the sections; each setting a row, its label and help on the left
 * and one control on the right, changing at once. What the design shows that isn't built yet says "(Coming later)".
 * PLAN.md §3, step 1: built from the component library (SectionNav, Section, SettingsRow and its controls).
 */
import { BOOK_LANGUAGES, METADATA_TEXT } from '@cockatrice/layout';
import type { BookLanguage, BookMetadata } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import { bindingProblem, COMMANDS, DEFAULT_KEYS, eventKey, keyParts } from '../shell/keys.ts';
import type { BindingProblem, Command } from '../shell/keys.ts';
import { PARAGRAPH_LOOKS } from '../shell/paragraph-look.ts';
import { EDITOR_FONTS, FONT_SIZES, PREVIEW_PROFILES } from '../shell/settings.ts';
import type { DeviceSettings } from '../shell/settings.ts';
import { el, each, when, whenSome } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import InlineConfirm from '../ui/InlineConfirm.ts';
import SectionNav from '../ui/SectionNav.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import Slider from '../ui/Slider.ts';
import TextField from '../ui/TextField.ts';
import { component } from '../ui/solid.ts';
import { AppSettings } from './pwa.ts';
import { BackupSettings } from './backup.ts';
import { PROFILE_NAMES } from './preview.ts';
import { Later, Row, Section } from './setting-rows.ts';
import { GoalField } from './words.ts';
import { AccountSections } from './account-page.ts';
import type { Child } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** The modifier keys, which alone never end a key capture (K9). */
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'OS']);

/** A key as the writer reads it (K8): symbols on a Mac (⌃⌥⇧⌘), words joined by + elsewhere. */
export function shownKey(shell: Shell, key: string | null): string {
  const { t } = shell;
  if (key === null) return t('keys.none');
  const parts = keyParts(key, shell.platform);
  if (parts === null) return key;
  const named = `key.${parts.name}`;
  const fn = /^F(\d+)$/u.exec(parts.name);
  // Part 11b: a function key alone (help's F1) is shown through the catalogue too, as every key with a modifier is.
  const name = isKeyWord(named) ? t(named) : fn === null ? parts.name : t('key.function', { number: Number(fn[1]) });
  if (shell.platform === 'mac') return `${parts.modifiers.map((modifier) => MAC_SYMBOLS[modifier]).join('')}${name}`;
  return parts.modifiers.reduceRight((rest, modifier) => t('key.join', { first: t(`key.${modifier}`), rest }), name);
}

const MAC_SYMBOLS = Object.freeze({ ctrl: '⌃', alt: '⌥', shift: '⇧', meta: '⌘' });

type KeyWord = 'key.PageUp' | 'key.PageDown' | 'key.Home' | 'key.End' | 'key.ArrowUp' | 'key.ArrowDown' | 'key.ArrowLeft' | 'key.ArrowRight' | 'key.Enter' | 'key.Backspace' | 'key.Delete' | 'key.Insert' | 'key.Space';

const KEY_WORDS: ReadonlySet<string> = new Set<KeyWord>([
  'key.PageUp',
  'key.PageDown',
  'key.Home',
  'key.End',
  'key.ArrowUp',
  'key.ArrowDown',
  'key.ArrowLeft',
  'key.ArrowRight',
  'key.Enter',
  'key.Backspace',
  'key.Delete',
  'key.Insert',
  'key.Space',
]);

function isKeyWord(key: string): key is KeyWord {
  return KEY_WORDS.has(key);
}

/** The text of a binding problem (K5). */
function problemText(shell: Shell, problem: BindingProblem): string {
  switch (problem.kind) {
    case 'invalid':
      return shell.t('keys.invalid');
    case 'typing':
      return shell.t('keys.typing');
    case 'editor':
      return shell.t('keys.editor');
    case 'taken':
      return shell.t('keys.taken', { command: shell.t(`command.${problem.command}`) });
  }
}

/** The key `event` pressed, if it can be `command`'s (K5, K9), or why not. */
function captured(shell: Shell, command: Command, event: KeyboardEvent): { readonly key: string } | { readonly problem: BindingProblem } {
  const key = eventKey(event);
  if (key === null) return { problem: { kind: 'invalid' } };
  const problem = bindingProblem(shell.workspace.keys(), command, key, shell.platform, shell.reserved);
  return problem === null ? { key } : { problem };
}

/** A labelled select of `choices` (G2), described by `help` when given. */
function choiceField<T extends string>(id: string, choices: readonly T[], words: (choice: T) => string, value: () => T, set: (choice: T) => void, help = false, disabled: () => boolean = () => false): Child {
  return component(Select, {
    id,
    options: choices.map((choice) => ({ value: choice, label: words(choice) })),
    get value() {
      return value();
    },
    get disabled() {
      return disabled();
    },
    'aria-describedby': help ? `${id}-help` : undefined,
    onChange: (picked: string) => {
      const chosen = choices.find((choice) => choice === picked);
      if (chosen !== undefined) set(chosen);
    },
  });
}

/** A checkbox (G4), labelled by its row. */
function checkField(id: string, value: () => boolean, set: (on: boolean) => void, help = false): Child {
  return component(Checkbox, {
    id,
    get checked() {
      return value();
    },
    'aria-describedby': help ? `${id}-help` : undefined,
    onChange: (on: boolean) => {
      set(on);
    },
  });
}

const THEMES = ['dark', 'light', 'sepia', 'system'] as const;

/** SP1–SP4: how the app looks. */
function Appearance(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, workspace } = shell;
  const settings = workspace.settings;
  const change = (patch: Partial<DeviceSettings>): void => void workspace.updateSettings(patch);
  return Section(
    'settings-appearance',
    t('settings.appearance'),
    heading,
    Row(
      'setting-theme',
      t('settings.theme'),
      t('settings.themeHelp'),
      component(SegmentedControl, {
        'aria-labelledby': 'setting-theme-label',
        options: THEMES.map((theme) => ({ value: theme, label: t(`theme.${theme}`) })),
        get value() {
          return settings().editorTheme;
        },
        onChange: (picked: string) => {
          // The control reports only its own options: each picks its theme.
          for (const editorTheme of THEMES.filter((theme) => theme === picked)) change({ editorTheme });
        },
      }),
      'group',
    ),
    Row('setting-font', t('settings.font'), null, choiceField('setting-font', EDITOR_FONTS, (font) => t(`font.${font}`), () => settings().editorFont, (editorFont) => { change({ editorFont }); })),
    Row(
      'setting-size',
      t('settings.size'),
      t('settings.sizeHelp'),
      component(Slider, {
        id: 'setting-size',
        class: 'set-range',
        min: FONT_SIZES.min,
        max: FONT_SIZES.max,
        step: FONT_SIZES.step,
        get value() {
          return settings().fontSize;
        },
        format: (size: number) => t('settings.sizeValue', { size }),
        'aria-describedby': 'setting-size-help',
        onChange: (fontSize: number) => {
          change({ fontSize });
        },
      }),
    ),
    Row('setting-look', t('settings.paragraphLook'), null, choiceField('setting-look', PARAGRAPH_LOOKS, (look) => t(`look.${look}`), () => settings().paragraphLook, (paragraphLook) => { change({ paragraphLook }); })),
  );
}

/** SP5–SP9: writing. */
function Writing(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, workspace, locale } = shell;
  const settings = workspace.settings;
  const change = (patch: Partial<DeviceSettings>): void => void workspace.updateSettings(patch);
  const names = new Intl.DisplayNames(locale, { type: 'language' });
  return Section(
    'settings-writing',
    t('settings.writing'),
    heading,
    // SP5: the open book's daily goal, as the word count dialog sets it (G2, G3).
    el('div', { class: 'c-setting c-setting--goal' }, GoalField(shell, 'dailyGoal', t('settings.dailyGoal'), () => '', 'setting-goal'), el('p', { class: 'c-setting__help' }, t('settings.dailyGoalHelp'))),
    Later(shell, t('settings.typewriter'), t('settings.typewriterHelp')),
    Later(shell, t('settings.smartQuotes'), t('settings.smartQuotesHelp')),
    Row('setting-conversion', t('settings.autoConversion'), t('settings.autoConversionHint'), checkField('setting-conversion', () => settings().autoConversion, (autoConversion) => { change({ autoConversion }); }, true), 'label', true),
    Row('setting-focus', t('settings.focusMode'), null, checkField('setting-focus', () => settings().focusMode, (focusMode) => { change({ focusMode }); }), 'label', true),
    // SP9: the book's language, which the editor's spelling and the pages' hyphenation follow (B13).
    whenSome(readableDetails(shell), (metadata) =>
      Row(
        'setting-language',
        t('settings.spelling'),
        t('settings.spellingHelp'),
        choiceField<BookLanguage>('setting-language', BOOK_LANGUAGES, (language) => languageName(names, language), () => metadata().language, (language) => void workspace.setLanguage(language), true, () => !workspace.writable()),
      ),
    ),
  );
}

/** SP11, SP12: what exports and the pages start from. */
function Exports(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, workspace } = shell;
  return Section(
    'settings-exports',
    t('settings.exports'),
    heading,
    Row(
      'setting-page-size',
      t('settings.pageSize'),
      t('settings.pageSizeHelp'),
      choiceField('setting-page-size', PREVIEW_PROFILES, (profile) => t(PROFILE_NAMES[profile]), () => workspace.settings().previewProfile, (previewProfile) => void workspace.updateSettings({ previewProfile, previewDevice: null }), true),
    ),
    Later(shell, t('settings.sceneBreak'), t('settings.sceneBreakHelp')),
    Later(shell, t('settings.manuscript'), t('settings.manuscriptHelp')),
  );
}

/** The shortcuts section (K1, K5, K9): each command's key, change it by pressing the new keys, remove it, reset all. */
function KeySettings(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, workspace, ui } = shell;
  const [problem, setProblem] = createSignal<string | null>(null);
  const capture = (command: Command, event: KeyboardEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Escape') {
      ui.setCapturing(null);
      setProblem(null);
      return;
    }
    if (MODIFIER_KEYS.has(event.key)) return;
    const result = captured(shell, command, event);
    if ('problem' in result) {
      setProblem(problemText(shell, result.problem));
      return;
    }
    setProblem(null);
    ui.setCapturing(null);
    void workspace.updateSettings({ keys: { ...workspace.keys(), [command]: result.key } });
  };
  return Section(
    'settings-keys',
    t('settings.keys'),
    heading,
    el(
      'ul',
      { class: 'shortcuts' },
      each(
        () => COMMANDS,
        (command) => {
          let change: HTMLButtonElement | undefined;
          return el(
            'li',
            {},
            el('span', { class: 'command' }, t(`command.${command}`)),
            el('kbd', {}, () => shownKey(shell, workspace.keys()[command])),
            component(Button, {
              size: 'sm',
              ref: (button: HTMLButtonElement) => {
                change = button;
              },
              // The full name, which starts with the words shown (2.5.3).
              'aria-label': t('keys.change', { command: t(`command.${command}`) }),
              'aria-pressed': () => ui.capturing() === command,
              onClick: () => {
                setProblem(null);
                ui.setCapturing(command);
                // Safari and macOS Firefox don't focus a clicked button, and the keys go where the focus is (*deep-fix r6*).
                change?.focus();
              },
              onKeyDown: (event: KeyboardEvent) => {
                if (ui.capturing() === command) capture(command, event);
              },
              onBlur: () => {
                if (ui.capturing() === command) ui.setCapturing(null);
              },
              children: t('keys.changeShort'),
            }),
            component(Button, {
              variant: 'ghost',
              size: 'sm',
              'aria-label': t('keys.remove', { command: t(`command.${command}`) }),
              get disabled() {
                return workspace.keys()[command] === null;
              },
              onClick: () => {
                void workspace.updateSettings({ keys: { ...workspace.keys(), [command]: null } });
                // Part 11c, A24: the button turns disabled, which drops the focus; the command's Change takes it.
                change?.focus();
              },
              children: t('keys.removeShort'),
            }),
          );
        },
      ),
    ),
    el('p', { class: 'capture', role: 'status' }, () => {
      const command = ui.capturing();
      return problem() ?? (command === null ? '' : t('keys.press', { command: t(`command.${command}`) }));
    }),
    ResetKeys(shell),
  );
}

/**
 * Part 11c, A22 (3.3.6: a destructive action is confirmed or undoable): "Reset every shortcut" asks first, since the
 * writer's own keys can't be had back; Keep mine is the first choice. Either answer, or Escape, brings the button back
 * with the focus (A24).
 */
function ResetKeys(shell: Shell): Child {
  const { t, workspace } = shell;
  return component(InlineConfirm, {
    label: t('keys.reset'),
    message: t('keys.resetAsk'),
    cancelLabel: t('keys.resetKeep'),
    confirmLabel: t('keys.resetConfirm'),
    triggerClass: 'reset-keys-start ui-inline-confirm__trigger ui-inline-confirm__trigger--danger',
    onConfirm: () => void workspace.updateSettings({ keys: DEFAULT_KEYS }),
  });
}

/** A language's name in the interface's language, or its tag when the browser has none (B13). */
export function languageName(names: { of(code: string): string | undefined }, tag: string): string {
  return names.of(tag) ?? tag;
}

/** The open book's title's field (B12; part 11d, H8: in Book details; part 11e, SP15: in the settings' This book). */
function TitleInput(shell: Shell, id: string, metadata: () => BookMetadata): Child {
  const { workspace } = shell;
  return component(TextField, {
    id,
    maxLength: METADATA_TEXT.title.max,
    get value() {
      return metadata().title;
    },
    // Part 36a, RO: the title is read-only while the books are.
    readOnly: () => !workspace.writable(),
    onCommit: (title: string) => {
      void workspace.renameBook(title);
    },
  });
}

/** The open book's details while they can be read (B3), as an accessor that is never null. */
function readableDetails(shell: Shell): () => BookMetadata | null {
  return () => shell.workspace.book()?.metadata ?? null;
}

/** SP15: the open book's title; for a book whose details can't be read (B3), what it is, and no field. */
function ThisBook(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, workspace } = shell;
  return Section(
    'settings-book',
    t('settings.book'),
    heading,
    when(
      () => (workspace.book()?.metadata ?? null) === null,
      () => el('p', { class: 'c-setting__help' }, t('book.unreadable')),
    ),
    whenSome(readableDetails(shell), (metadata) => Row('setting-book-title', t('book.title'), null, TitleInput(shell, 'setting-book-title', metadata))),
  );
}

/** SP17, SP18: the account and the guide. */
function AccountAndHelp(shell: Shell, account: (node: HTMLElement) => void, help: (node: HTMLElement) => void): Child {
  const { t, ui } = shell;
  return [
    AccountSections(shell, account),
    Section(
      'settings-help',
      t('settings.help'),
      help,
      Row(
        'setting-guide',
        t('settings.guide'),
        t('settings.guideHelp'),
        component(Button, { id: 'setting-guide', 'aria-haspopup': 'dialog', 'aria-describedby': 'setting-guide-help', onClick: () => ui.setDialog({ kind: 'help', first: false }), children: t('settings.guide') }),
      ),
    ),
  ];
}

/** Part 11e, SL2: leaves the settings for the page they were opened from (Back), else home. */
export function leaveSettings(shell: Shell): void {
  if (shell.ui.settingsFrom() === null) shell.go('home');
  else shell.back();
}

/** The sections in the page's order: each one's ID and title, and what makes it given where its heading goes. */
interface SectionEntry {
  readonly id: string;
  readonly title: string;
  readonly make: (heading: (node: HTMLElement) => void) => Child;
}

/**
 * The settings page (SP1–SP18, SL1–SL5): its header with the way back, the job statuses, the section list, and the
 * sections. A section's entry scrolls to it and puts the focus on its heading.
 */
export function SettingsPage(shell: Shell, statuses: () => Child): Child {
  const { t, ui } = shell;
  const headings = new Map<string, HTMLElement>();
  const at = (id: string) => (node: HTMLElement): void => {
    headings.set(id, node);
  };
  const sections: readonly SectionEntry[] = [
    { id: 'settings-appearance', title: t('settings.appearance'), make: (heading) => Appearance(shell, heading) },
    { id: 'settings-writing', title: t('settings.writing'), make: (heading) => Writing(shell, heading) },
    { id: 'settings-keys', title: t('settings.keys'), make: (heading) => KeySettings(shell, heading) },
    { id: 'settings-exports', title: t('settings.exports'), make: (heading) => Exports(shell, heading) },
    { id: 'settings-backups', title: t('settings.backupsSync'), make: (heading) => BackupSettings(shell, heading) },
    { id: 'settings-book', title: t('settings.book'), make: (heading) => ThisBook(shell, heading) },
    { id: 'settings-app', title: t('settings.app'), make: (heading) => AppSettings(shell, heading) },
  ];
  // Part 36a: opened at a section (the account button), that section's entry is the current one (the app's page
  // focus puts the focus on its heading).
  const [current, setCurrent] = createSignal(ui.settingsAt() ?? 'settings-appearance');
  const jump = (id: string): void => {
    setCurrent(id);
    for (const [key, heading] of headings) {
      if (key !== id) continue;
      heading.scrollIntoView({ block: 'start' });
      heading.focus();
    }
  };
  const entries: readonly { readonly id: string; readonly title: string }[] = [...sections, { id: 'settings-account', title: t('settings.account') }, { id: 'settings-help', title: t('settings.help') }];

  return el(
    'div',
    { class: 'set' },
    el(
      'header',
      { class: 'set-header' },
      component(Button, {
        variant: 'ghost',
        size: 'sm',
        iconStart: 'back',
        class: 'set-back',
        onClick: () => {
          leaveSettings(shell);
        },
        get children() {
          return ui.settingsFrom() === 'write' || ui.settingsFrom() === 'format' ? t('settings.backWrite') : t('settings.backHome');
        },
      }),
      el('h1', { id: 'settings-title', tabIndex: -1 }, t('settings.title')),
    ),
    statuses(),
    el(
      'div',
      { class: 'set-layout' },
      component(SectionNav, {
        class: 'set-nav',
        label: t('settings.sections'),
        items: entries.map((entry) => ({
          label: entry.title,
          get current() {
            return current() === entry.id;
          },
          onSelect: () => {
            jump(entry.id);
          },
        })),
      }),
      el('main', { class: 'set-main' }, ...sections.map((section) => section.make(at(section.id))), AccountAndHelp(shell, at('settings-account'), at('settings-help'))),
    ),
  );
}
